import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Building2, Users, Clock, AlertTriangle, ShieldAlert, MapPin, TrendingUp,
} from 'lucide-react';

const COLORS = ['#FF9933', '#138808', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4', '#EF4444'];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchAnalytics();
  }, [month]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics?month=${month}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500 text-lg font-medium">Loading analytics...</div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, branches } = data;
  const activeBranches = branches.filter(b => b.location_id !== 'unassigned');

  // Chart data
  const attendanceChartData = activeBranches.map(b => ({
    name: b.location_name.length > 12 ? b.location_name.slice(0, 12) + '...' : b.location_name,
    fullName: b.location_name,
    rate: b.attendance_rate,
    checkins: b.total_checkins,
  }));

  const headcountData = activeBranches.map((b, i) => ({
    name: b.location_name,
    value: b.employee_count,
    color: COLORS[i % COLORS.length],
  })).filter(d => d.value > 0);

  const hoursChartData = activeBranches.map(b => ({
    name: b.location_name.length > 12 ? b.location_name.slice(0, 12) + '...' : b.location_name,
    fullName: b.location_name,
    avgHours: b.avg_hours,
    lateCount: b.late_count,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const fullName = payload[0]?.payload?.fullName || label;
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
          <p className="font-semibold text-slate-800 mb-1">{fullName}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-xs">
              {entry.name}: {entry.value}{entry.name === 'rate' ? '%' : entry.name === 'avgHours' ? 'h' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Branch Analytics</h1>
          <p className="text-slate-500">Compare performance across all branches</p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="month-picker" className="text-sm text-slate-600 whitespace-nowrap">Month</Label>
          <Input
            id="month-picker"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44"
            data-testid="analytics-month-picker"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="analytics-total-branches">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-purple-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 truncate">Total Branches</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_branches}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="analytics-total-employees">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 truncate">Total Employees</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_employees}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="analytics-total-attendance">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 truncate">Total Check-ins</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_attendance}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="analytics-total-out-of-range">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-5 h-5 text-red-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 truncate">Out of Range</p>
                <p className="text-xl font-bold text-slate-800">{summary.total_out_of_range}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Rate by Branch */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#FF9933]" />
              Attendance Rate by Branch
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0" data-testid="attendance-rate-chart">
            {attendanceChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={attendanceChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, 100]} unit="%" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="rate" fill="#FF9933" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm text-center py-12">No attendance data for this month</p>
            )}
          </CardContent>
        </Card>

        {/* Headcount Distribution */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              Employee Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0" data-testid="headcount-chart">
            {headcountData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={headcountData}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={45}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name.slice(0, 10)}(${value})`}
                  >
                    {headcountData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm text-center py-12">No employee data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Avg Hours & Late Arrivals Chart */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="w-4 h-4 text-green-600" />
            Avg Work Hours &amp; Late Arrivals by Branch
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0" data-testid="hours-late-chart">
          {hoursChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hoursChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="avgHours" name="Avg Hours" fill="#138808" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="lateCount" name="Late Arrivals" fill="#EF4444" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-slate-400 text-sm text-center py-12">No data for this month</p>
          )}
        </CardContent>
      </Card>

      {/* Branch Details Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-purple-500" />
            Branch-wise Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-testid="branch-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Branch</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Employees</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Check-ins</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Att. Rate</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Avg Hours</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Late</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Leaves</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Out of Range</th>
                  <th className="text-center py-3 px-3 font-semibold text-slate-600">Geofence</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((branch) => (
                  <tr key={branch.location_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`branch-row-${branch.location_id}`}>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-slate-800">{branch.location_name}</p>
                        <p className="text-xs text-slate-400 truncate max-w-[180px]">{branch.address}</p>
                      </div>
                    </td>
                    <td className="text-center py-3 px-3 text-slate-700">{branch.employee_count}</td>
                    <td className="text-center py-3 px-3 text-slate-700">{branch.total_checkins}</td>
                    <td className="text-center py-3 px-3">
                      <Badge
                        className={`${
                          branch.attendance_rate >= 80
                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                            : branch.attendance_rate >= 50
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                            : 'bg-red-100 text-red-700 hover:bg-red-100'
                        }`}
                      >
                        {branch.attendance_rate}%
                      </Badge>
                    </td>
                    <td className="text-center py-3 px-3 text-slate-700">{branch.avg_hours}h</td>
                    <td className="text-center py-3 px-3">
                      {branch.late_count > 0 ? (
                        <span className="flex items-center justify-center gap-1 text-red-600">
                          <AlertTriangle className="w-3 h-3" />
                          {branch.late_count}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3 text-slate-700">{branch.approved_leaves}</td>
                    <td className="text-center py-3 px-3">
                      {branch.out_of_range_count > 0 ? (
                        <Badge variant="destructive" className="text-xs">
                          {branch.out_of_range_count}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      {branch.location_id !== 'unassigned' ? (
                        <span className="text-xs text-slate-500">{branch.geofence_radius}m / {branch.geofence_action}</span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
